import React, { useEffect, useState } from "react";

import { faEye, faText } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "antd/es/grid";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import EmailDynamicVariable from "@mapmycustomers/shared/types/email/EmailDynamicVariable";
import { EntityTypesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import SparklesIcon from "../../../../assets/ai-sparkle.jsx.svg";
import { useConfigProvider } from "../../../../ConfigProvider";
import ActionCallback from "../../type/ActionCallback";
import FileAttachCallback from "../../type/FileAttachCallback";
import ImageUploadCallback from "../../type/ImageUploadCallback";
import ResourceLinkInsertCallback from "../../type/ResourceLinkInsertCallback";
import LinkCreate from "../LinkCreate";
import LinkInsert from "../LinkInsert";
import ToggleButton from "../ToggleButton";
import Toolbar from "../Toolbar";

import styles from "./Actions.module.scss";
import DynamicVariables from "./DynamicVariables";
import FilesAttach from "./FilesAttach";
import ResourceLinkInsert from "./ResourceLinkInsert";
import UnsubscribeLink from "./UnsubscribeLink";

interface ActionsProps {
  action?: React.ReactNode;
  aiWriterVisible?: boolean;
  dynamicVariables?: EmailDynamicVariable[];
  entityType?: EntityTypesSupportedByEmailFeature;
  onAction?: ActionCallback;
  onAiWriterToggle?: () => void;
  onFileAttach?: FileAttachCallback;
  onImageUpload: ImageUploadCallback;
  onPreview?: () => void;
  onResourceLinkInsert?: ResourceLinkInsertCallback;
  onUnsubscribeLinkIncludedChange?: (value: boolean) => void;
  previewDisableText?: string;
  recipientCount?: number;
  reply?: boolean;
  unsubscribeLinkIncluded?: boolean;
}

const Actions = ({
  action,
  aiWriterVisible,
  dynamicVariables,
  entityType,
  onAction,
  onAiWriterToggle,
  onFileAttach,
  onImageUpload,
  onPreview,
  onResourceLinkInsert,
  onUnsubscribeLinkIncludedChange,
  previewDisableText,
  recipientCount,
  reply,
  unsubscribeLinkIncluded,
}: ActionsProps) => {
  const [insetLinkVisible, setInsetLinkVisible] = useState(false);
  const configProvider = useConfigProvider();
  const [toolbarVisible, setToolbarVisibility] = useState(false);

  useChangeTracking(() => {
    if (toolbarVisible) {
      onAction?.("Text Formatting Bar");
    }
  }, [toolbarVisible]);

  const multipleRecipients = recipientCount !== undefined && recipientCount > 1;

  useEffect(() => {
    if (!reply && !multipleRecipients && unsubscribeLinkIncluded) {
      onUnsubscribeLinkIncludedChange?.(false);
    }
  }, [multipleRecipients, reply, unsubscribeLinkIncluded, onUnsubscribeLinkIncludedChange]);

  return (
    <div className={styles.container}>
      {unsubscribeLinkIncluded !== undefined &&
        onUnsubscribeLinkIncludedChange &&
        multipleRecipients && (
          <UnsubscribeLink
            onUnsubscribeLinkIncludedChange={onUnsubscribeLinkIncludedChange}
            unsubscribeLinkIncluded={unsubscribeLinkIncluded}
          />
        )}
      <Row align="middle" className={styles.actionContainer} justify="space-between">
        <Col>
          <Row align="middle">
            <Col>
              <Popover
                content={<Toolbar onImageUpload={onImageUpload} />}
                destroyTooltipOnHide={false}
                onOpenChange={setToolbarVisibility}
                overlayClassName={styles.toolbarPopover}
                placement="topLeft"
                trigger="click"
              >
                <Tooltip title={configProvider.formatMessage("ui.emailBody.actions.formatting")}>
                  <ToggleButton active={toolbarVisible}>
                    <FontAwesomeIcon icon={faText} />
                  </ToggleButton>
                </Tooltip>
              </Popover>
            </Col>
            <Col>
              <LinkInsert onAction={onAction} />
            </Col>
            {onFileAttach && (
              <Col>
                <FilesAttach onAction={onAction} onAttach={onFileAttach} />
              </Col>
            )}
            {onResourceLinkInsert && (
              <Col>
                <ResourceLinkInsert onInsert={onResourceLinkInsert} />
              </Col>
            )}
            {dynamicVariables && entityType && (
              <Col>
                <DynamicVariables
                  dynamicVariables={dynamicVariables}
                  entityType={entityType}
                  onAction={onAction}
                />
              </Col>
            )}
            {onPreview && (
              <Col>
                <Tooltip title={configProvider.formatMessage("ui.emailBody.actions.preview")}>
                  <Tooltip title={previewDisableText} trigger={previewDisableText ? ["hover"] : []}>
                    <ToggleButton onClick={previewDisableText ? undefined : onPreview}>
                      <FontAwesomeIcon
                        className={cn({
                          [styles.disabledIcon]: !!previewDisableText,
                        })}
                        icon={faEye}
                      />
                    </ToggleButton>
                  </Tooltip>
                </Tooltip>
              </Col>
            )}
            {onAiWriterToggle && (
              <Col>
                <Tooltip title={configProvider.formatMessage("ui.emailBody.actions.generateEmail")}>
                  <ToggleButton
                    className={cn(styles.aiWriter, {
                      [styles.aiWriterVisible]: aiWriterVisible,
                    })}
                    onClick={onAiWriterToggle}
                  >
                    <SparklesIcon />
                  </ToggleButton>
                </Tooltip>
              </Col>
            )}
          </Row>
        </Col>
        <Col>{action}</Col>
      </Row>
      {insetLinkVisible && <LinkCreate onHide={() => setInsetLinkVisible(false)} />}
    </div>
  );
};

export default Actions;
